import type { ChangeEvent } from "react";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import { endOfMonth, format, startOfMonth, subDays } from "date-fns";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  FINANCEIRO_ACCENT,
  financeiroToolbarSx,
} from "../../components/financeiro-lancamentos.theme";
import type {
  DashboardFinanceiroFiltros,
  GranularidadeFinanceira,
} from "../types/financeiro-dashboard.types";

export type PeriodoRapido = "hoje" | "mes" | "30d" | "personalizado";

function formatYmd(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

type Props = {
  filtros: DashboardFinanceiroFiltros;
  onChange: (next: DashboardFinanceiroFiltros) => void;
  onApply: (override?: DashboardFinanceiroFiltros) => void;
  periodoRapido: PeriodoRapido;
  onPeriodoRapidoChange: (p: PeriodoRapido) => void;
  /** Oculta granularidade quando a tela só exibe totais (ex.: dashboard geral). */
  showGranularidade?: boolean;
};

const OPCOES_PERIODO: { value: Exclude<PeriodoRapido, "personalizado">; label: string }[] = [
  { value: "hoje", label: "Hoje" },
  { value: "mes", label: "Este mês" },
  { value: "30d", label: "30 dias" },
];

const OPCOES_GRANULARIDADE: { value: GranularidadeFinanceira; label: string }[] = [
  { value: "diaria", label: "Diária" },
  { value: "semanal", label: "Semanal" },
  { value: "mensal", label: "Mensal" },
  { value: "anual", label: "Anual" },
];

const toggleSx = {
  flexWrap: "wrap" as const,
  "& .MuiToggleButton-root": {
    px: 1.25,
    py: 0.75,
    textTransform: "none" as const,
    fontSize: "0.8125rem",
    "&.Mui-selected": {
      bgcolor: "rgba(249, 96, 48, 0.1)",
      borderColor: FINANCEIRO_ACCENT,
      color: FINANCEIRO_ACCENT,
      fontWeight: 600,
    },
  },
};

const fieldSx = { bgcolor: "background.paper", borderRadius: 1.5 };

export default function FinanceiroFiltroPeriodo({
  filtros,
  onChange,
  onApply,
  periodoRapido,
  onPeriodoRapidoChange,
  showGranularidade = true,
}: Props) {
  const aplicarPeriodoRapido = (p: Exclude<PeriodoRapido, "personalizado">) => {
    onPeriodoRapidoChange(p);
    const hoje = new Date();
    let next: DashboardFinanceiroFiltros = { ...filtros };
    if (p === "hoje") {
      const y = formatYmd(hoje);
      next = { ...filtros, dataInicio: y, dataFim: y };
    } else if (p === "mes") {
      next = {
        ...filtros,
        dataInicio: formatYmd(startOfMonth(hoje)),
        dataFim: formatYmd(endOfMonth(hoje)),
      };
    } else if (p === "30d") {
      next = {
        ...filtros,
        dataInicio: formatYmd(subDays(hoje, 29)),
        dataFim: formatYmd(hoje),
      };
    }
    onChange(next);
    onApply(next);
  };

  const periodoToggleValue =
    periodoRapido === "personalizado" ? "" : periodoRapido;

  const patchData =
    (field: "dataInicio" | "dataFim") =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onPeriodoRapidoChange("personalizado");
      onChange({
        ...filtros,
        [field]: e.target.value || undefined,
      });
    };

  return (
    <MDBox sx={financeiroToolbarSx}>
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <DateRangeOutlinedIcon sx={{ color: FINANCEIRO_ACCENT }} />
          <MDTypography variant="button" fontWeight="medium" color="text">
            Filtros operacionais
          </MDTypography>
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Período
          </Typography>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", lg: "flex-end" }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={periodoToggleValue}
              onChange={(_, next: Exclude<PeriodoRapido, "personalizado"> | null) => {
                if (next) aplicarPeriodoRapido(next);
              }}
              sx={{ ...toggleSx, flexShrink: 0 }}
            >
              {OPCOES_PERIODO.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <FormControl variant="outlined" size="small" sx={{ minWidth: 160, flex: 1 }}>
              <InputLabel shrink htmlFor="fin-per-inicio">
                Data início
              </InputLabel>
              <OutlinedInput
                id="fin-per-inicio"
                type="date"
                value={filtros.dataInicio ?? ""}
                onChange={patchData("dataInicio")}
                label="Data início"
                notched
                sx={fieldSx}
              />
            </FormControl>

            <FormControl variant="outlined" size="small" sx={{ minWidth: 160, flex: 1 }}>
              <InputLabel shrink htmlFor="fin-per-fim">
                Data fim
              </InputLabel>
              <OutlinedInput
                id="fin-per-fim"
                type="date"
                value={filtros.dataFim ?? ""}
                onChange={patchData("dataFim")}
                label="Data fim"
                notched
                sx={fieldSx}
              />
            </FormControl>
          </Stack>
        </Stack>

        {showGranularidade ? (
          <Stack spacing={0.75}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Granularidade
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={filtros.granularidade ?? "mensal"}
              onChange={(_, next: GranularidadeFinanceira | null) => {
                if (!next) return;
                onChange({ ...filtros, granularidade: next });
              }}
              sx={toggleSx}
            >
              {OPCOES_GRANULARIDADE.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        ) : null}

        <Divider />

        <Stack direction="row" justifyContent="flex-end">
          <MDButton
            variant="gradient"
            color="dark"
            size="small"
            onClick={() => onApply()}
            sx={{
              background: `linear-gradient(195deg, ${FINANCEIRO_ACCENT}, #e04a1a)`,
            }}
          >
            Aplicar filtros
          </MDButton>
        </Stack>
      </Stack>
    </MDBox>
  );
}
